import { useCallback, useMemo, useState } from 'react';
import { placementFor, resultFor, useApp, workerControl } from '../../app/store';
import { MAIN_METRICS, METRICS } from '../../app/metrics';
import { MAX_BUILDINGS } from '../../app/persist';
import { fmt, fmtDuration, fmtPct } from '../../app/format';
import { relativeChange } from '../../core/calc/engine';
import { MODELS, type MetricKey } from '../../core/domain/types';
import { chunkWeight } from '../../core/providers/openMeteo';
import { LocationSearch } from '../components/LocationSearch';
import { CsvImportDialog } from '../components/CsvImportDialog';
import { PortfolioMap } from '../components/LazyMaps';
import { QueueNotice } from '../components/Panels';
import { IconFuture, IconTrash, IconUpload } from '../icons';
import { go } from '../hooks';

type SortKey = 'label' | 'status' | MetricKey;

export function EmptyPortfolio({ withSearch = true }: { withSearch?: boolean }) {
  const add = useApp((s) => s.addBuildings);
  const setFocus = useApp((s) => s.setFocus);
  const mode = useApp((s) => s.mode);
  return (
    <div className="empty">
      <h2>Noch kein Gebäude im Portfolio</h2>
      <p>
        {withSearch ? 'Suchen Sie' : 'Suchen Sie oben'} eine Adresse, eine EGID, einen Ort oder geben Sie Koordinaten ein, oder importieren Sie eine CSV-Liste. Die Klimaanalyse startet sofort mit dem letzten vollständigen Jahr.
      </p>
      {withSearch && (
      <div style={{ maxWidth: 560, margin: '24px auto 0', textAlign: 'left' }}>
        <LocationSearch
          disabled={mode === 'demo'}
          disabledReason="Im Offline-Demomodus nicht verfügbar"
          onPick={(b) => {
            add([b]);
            setFocus(b.id);
          }}
        />
      </div>
      )}
    </div>
  );
}

export function PortfolioView() {
  const s = useApp();
  const [csvOpen, setCsvOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'label', dir: 1 });
  const cfg = s.config;
  const q = s.queue;

  const cellCount = useMemo(() => new Set(s.placements.filter((p) => p.model === cfg.model).map((p) => p.cellKey)).size, [s.placements, cfg.model]);
  const unplaced = s.buildings.filter((b) => !placementFor(s, b.id)).length;

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const list = s.buildings
      .filter((b) => !f || `${b.label} ${b.address ?? ''} ${b.egid ?? ''}`.toLowerCase().includes(f))
      .map((b) => {
        const r = resultFor(s, b.id);
        const last = r && r.lastCompleteYear !== null ? r.years.find((y) => y.year === r.lastCompleteYear) : undefined;
        return { b, r, last, p: placementFor(s, b.id) };
      });
    const val = (x: (typeof list)[number]): number | string => {
      if (sort.key === 'label') return x.b.label.toLowerCase();
      if (sort.key === 'status') return x.r ? x.r.cellLoadedYears : -1;
      return x.last ? (x.last[sort.key] as number) : Number.NEGATIVE_INFINITY;
    };
    return list.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
    });
  }, [s, filter, sort]);

  const onSelect = useCallback(
    (id: string) => {
      useApp.getState().setFocus(id);
    },
    [],
  );

  const headerBtn = (key: SortKey, label: React.ReactNode, numeric = false) => (
    <th className={numeric ? 'n' : undefined} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="sort" onClick={() => setSort({ key, dir: sort.key === key ? ((-sort.dir) as 1 | -1) : numeric ? -1 : 1 })}>
        {label}
        <span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );

  const weight = q ? q.weightRemaining : 0;
  const lastYearLabel = cfg.periodEnd;

  return (
    <div>
      <header className="page-head">
        <div>
          <div className="eyebrow">Portfolio</div>
          <h1>
            {s.buildings.length} {s.buildings.length === 1 ? 'Gebäude' : 'Gebäude'} · {cellCount} {cellCount === 1 ? 'Rasterzelle' : 'Rasterzellen'}
          </h1>
          <p className="muted" style={{ marginTop: 8, maxWidth: '70ch' }}>
            Gebäude derselben {MODELS[cfg.model].label}-Rasterzelle teilen einen Datenabruf; die Höhenkorrektur erfolgt je Gebäude. Kennzahlen: letztes vollständiges Jahr (bis {lastYearLabel}) und Abweichung vom Mittel 1991–2020. Keine Rangliste und kein
            Gesamtscore.
          </p>
        </div>
        {s.buildings.length > 0 && s.mode === 'live' && (
          <button type="button" className="btn" onClick={() => go('zukunft', { ansicht: 'portfolio' })}>
            <IconFuture /> Zukunftsklima des Portfolios
          </button>
        )}
      </header>

      <div className="row" style={{ alignItems: 'stretch' }}>
        <div style={{ flex: '1 1 420px', minWidth: 0 }}>
          <LocationSearch
            disabled={s.mode === 'demo'}
            disabledReason="Im Offline-Demomodus nicht verfügbar – auf «Live» wechseln"
            onPick={(b) => {
              const r = s.addBuildings([b]);
              if (r.skipped) s.notify('info', 'Gebäude bereits im Portfolio');
              if (r.overLimit) s.notify('warn', 'Portfolio voll', `Ein Portfolio fasst höchstens ${fmt(MAX_BUILDINGS, 0)} Gebäude.`);
            }}
          />
        </div>
        <button type="button" className="btn" style={{ minHeight: 48 }} disabled={s.mode === 'demo'} onClick={() => setCsvOpen(true)}>
          <IconUpload /> CSV importieren
        </button>
      </div>
      <CsvImportDialog open={csvOpen} onClose={() => setCsvOpen(false)} />

      <div style={{ marginTop: 16 }}>
        <QueueNotice />
      </div>

      <div className="grid-main" style={{ marginTop: 24 }}>
        <div className="stack" style={{ minWidth: 0 }}>
          {s.buildings.length === 0 ? (
            <EmptyPortfolio withSearch={false} />
          ) : (
            <>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <label className="row" style={{ gap: 8, flex: '1 1 240px' }}>
                  <span className="small" style={{ fontWeight: 600 }}>
                    Filtern
                  </span>
                  <input className="input" style={{ maxWidth: 320 }} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Bezeichnung, Adresse oder EGID" />
                </label>
                <span className="small muted">
                  Vergleich: {s.compareIds.length} von 5 gewählt{' '}
                  {s.compareIds.length > 0 && (
                    <button type="button" className="btn btn-sm" onClick={() => go('vergleich')}>
                      Vergleich öffnen
                    </button>
                  )}
                </span>
              </div>
              <div className="table-wrap" tabIndex={0} role="region" aria-label="Portfoliotabelle" style={{ maxHeight: 640 }}>
                <table className="data">
                  <caption className="sr-only">Gebäude mit Kennzahlen des letzten vollständigen Jahres</caption>
                  <thead>
                    <tr>
                      <th>
                        <span className="sr-only">Im Vergleich</span>Vgl.
                      </th>
                      {headerBtn('label', 'Gebäude')}
                      {headerBtn('status', 'Daten')}
                      {MAIN_METRICS.map((k) =>
                        headerBtn(
                          k,
                          <>
                            {METRICS[k].short} <span style={{ fontWeight: 400 }}>[{METRICS[k].unit}]</span>
                          </>,
                          true,
                        ),
                      )}
                      <th>
                        <span className="sr-only">Aktionen</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ b, r, last, p }) => {
                      const inCmp = s.compareIds.includes(b.id);
                      const fail = s.placementFailures[b.id];
                      return (
                        <tr key={b.id} className={b.id === s.focusId ? 'row-selected' : undefined}>
                          <td>
                            <input
                              type="checkbox"
                              checked={inCmp}
                              aria-label={`${b.label} im Vergleich`}
                              onChange={() => {
                                if (!s.toggleCompare(b.id)) s.notify('warn', 'Höchstens fünf Standorte im Vergleich');
                              }}
                            />
                          </td>
                          <td style={{ minWidth: 200 }}>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              style={{ padding: 0, minHeight: 0, fontWeight: 600, whiteSpace: 'normal', textAlign: 'left', color: 'var(--primary)' }}
                              onClick={() => {
                                s.setFocus(b.id);
                                go('uebersicht');
                              }}
                            >
                              {b.label}
                            </button>
                            <div className="xsmall muted">
                              {[b.address && b.address !== b.label ? b.address : null, b.egid ? `EGID ${b.egid}` : null, p ? `${fmt(p.targetElevation)} m` : null].filter(Boolean).join(' · ')}
                            </div>
                          </td>
                          <td className="xsmall" style={{ whiteSpace: 'nowrap' }}>
                            {fail ? <span className="chip chip-danger">{fail}</span> : !p ? 'Zelle wird zugeordnet' : r ? `${r.cellLoadedYears}/${r.cellTotalYears} Jahre` : 'lädt'}
                          </td>
                          {MAIN_METRICS.map((k) => {
                            const m = METRICS[k];
                            const v = last ? (last[k] as number) : null;
                            const ref = r && r.reference.status !== 'ungenuegend' ? r.reference.means[k] : undefined;
                            const rel = v !== null && ref !== undefined ? relativeChange(v, ref) : null;
                            return (
                              <td key={k} className="n">
                                {v === null ? '–' : fmt(v, m.decimals)}
                                <div className="xsmall muted">{v === null ? '' : rel === null ? (ref === undefined ? 'ohne Ref.' : 'Ref. 0') : fmtPct(rel)}</div>
                              </td>
                            );
                          })}
                          <td>
                            <button type="button" className="icon-btn" aria-label={`${b.label} entfernen`} onClick={() => s.removeBuilding(b.id)} disabled={s.mode === 'demo'}>
                              <IconTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="xsmall muted">
                Werte: letztes vollständiges Jahr je Gebäude; darunter relative Abweichung vom Mittel 1991–2020 (bei Referenz 0 nicht definiert). Sortieren ordnet nach einer einzelnen Kennzahl und ist keine Bewertung.
              </p>
            </>
          )}
        </div>
        <aside className="stack">
          <PortfolioMap buildings={s.buildings} placements={s.placements} model={cfg.model} focusId={s.focusId} onSelect={onSelect} height={360} />
          <div className="panel">
            <h3>Abrufplan</h3>
            <dl className="dl" style={{ marginTop: 12 }}>
              <dt>Rasterzellen</dt>
              <dd>
                {cellCount}
                {unplaced ? ` (+${unplaced} Gebäude ohne Zuordnung)` : ''}
              </dd>
              <dt>Jahresstücke</dt>
              <dd>{q ? `${q.chunksDone} von ${q.chunksTotal} geladen` : '–'}</dd>
              {s.mode === 'live' && (
                <>
                  <dt>Offenes Volumen</dt>
                  <dd>{fmt(weight)} gewichtete Aufrufe ({fmt(chunkWeight(), 1)} je Zelle und Jahr)</dd>
                  <dt>Geschätzte Dauer</dt>
                  <dd>{q ? (s.keyed ? 'ohne Kontingentgrenze (Schlüssel)' : fmtDuration(q.etaMs)) : '–'}</dd>
                  <dt>Aus Browser-Cache</dt>
                  <dd>{q ? q.chunksFromCache : 0} Jahresstücke</dd>
                </>
              )}
            </dl>
            {s.mode === 'live' && !s.keyed && (
              <p className="xsmall muted" style={{ marginTop: 12 }}>
                Freie Open-Meteo-API (nur nicht-kommerziell): rund 5’000 gewichtete Aufrufe pro Stunde und 10’000 pro Tag. Grössere Portfolios laden über mehrere Stunden oder Tage; der Fortschritt bleibt im Browser gespeichert.
              </p>
            )}
            {s.mode === 'live' && (
              <div className="row" style={{ marginTop: 12 }}>
                <button type="button" className="btn btn-sm" onClick={workerControl.pause}>
                  Pausieren
                </button>
                <button type="button" className="btn btn-sm" onClick={workerControl.resume}>
                  Fortsetzen
                </button>
              </div>
            )}
          </div>
          {s.buildings.length > 0 && s.mode === 'live' && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: 'flex-start', color: 'var(--danger)' }}
              onClick={() => {
                if (confirm(`Alle ${s.buildings.length} Gebäude aus dem Portfolio entfernen? Geladene Klimadaten bleiben im Browser-Cache.`)) s.removeBuildings(s.buildings.map((b) => b.id));
              }}
            >
              <IconTrash /> Portfolio leeren
            </button>
          )}
        </aside>
      </div>
    </div>
  );
}
